import {
	AttachmentValidationStatus,
	LifeCycleStage,
	type PermissionSet,
	RequestListStatus,
	type RequestListTaskDto,
	RequestListTaskStatus,
} from '@sharefiledev/request-list-pilet-sdk';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { folderTabKey } from './folderTabEntry';

export {
	type RequestListTaskDto,
	type PermissionSet,
	type PermissionEvaluation,
	type RequestListSpec,
	type RequestListTaskSpec,
} from '@sharefiledev/request-list-pilet-sdk';
export {
	AttachmentValidationStatus,
	LifeCycleStage,
	PermissionFailureReason,
	PermissionPolicy,
	RequestListStatus,
	RequestListTaskStatus,
} from '@sharefiledev/request-list-pilet-sdk';

export type ExtensionImplementationProps<TParams> = TParams & { piletApi: PiletApi };

export interface ErrorReponse {
	StatusCode: number;
	Message: string;
	ErrorData?: any;
}

export enum RequestListEntityType {
	requestlist = 'requestlist',
	item = 'requestlist_item',
}

export enum ResourceEntityType {
	account = 'account',
	user = 'user',
}

export interface RequestListDto {
	requestListId: string;
	resourceId: string;
	createdBy: string;
	title: string;
	destinationId: string;
	status: RequestListStatus;
	description?: string;
	dueDate?: Date | null;
	startedOn?: Date;
	completedOn?: Date;
	actorIds: string[];
	taskSummary?: RequestListTaskSummary;
	attachmentDestinationId?: string;
	permissions?: PermissionSet;
	deletedOn?: Date;
	deletedByUserId?: string;
	enableFileBrowser?: boolean;
	lifeCycleStage?: LifeCycleStage;
}

export type RequestListUpdateDto = RequestListDto & DispatchNotificationPayload;

export interface ActivationPayload {
	notificationMessage?: string;
	attachmentDestinationId: string;
}

export type CreateRequestListState =
	| undefined
	| {
			requestList: RequestListDto;
			fetchedAt?: number;
	  };

export type CreateRequestListTaskPayload = Pick<
	RequestListTaskDto,
	'title' | 'description' | 'actorIds' | 'dueDate' | 'categories'
> & { sendEmail?: boolean };

export interface RequestListTaskSummary {
	statusCount: RequestListStatusCount;
	total: number;
}

export type RequestListStatusCount = Record<RequestListTaskStatus, number>;

export interface RequestListAttachmentDto extends CreateRequestListAttachmentPayload {
	attachmentId: string;
	createdOn: Date;
	createdBy: string;
	lastModifiedOn: Date;
	lastModifiedBy: string;
	requestListTaskId: string;
	requestListTaskTitle: string;
	uploadedByID: string;
	zoneID: string;
	permissions?: PermissionSet;
	lifeCycleStage?: LifeCycleStage;
	validationResult?: AttachmentValidationResult;
}

export interface AttachmentValidationResult {
	jobId: string;
	status: AttachmentValidationStatus;
	confidenceScore?: number;
	message?: string;
	recommendedFileName?: string;
}

export interface CreateRequestListAttachmentPayload {
	streamId: string;
	attachmentName: string;
	attachmentItemId: string;
}

export interface TaskActionPayload {
	message?: string;
}

export interface AddActorsPayload extends DispatchNotificationPayload {
	actorIds: string[];
	deferGrantPermissions?: boolean;
}

export interface RequestListUsersPayload extends AddActorsPayload {
	assignTasks?: boolean;
}

export interface DispatchNotificationPayload {
	sendEmail?: boolean;
	notificationMessage?: string;
}

export interface Paging {
	pageSize?: number;
	continuationToken?: string;
}

export interface PagedResponse<T> {
	items: T[];
	continuation?: { token?: string };
	permissions: PermissionSet;
	itemCategories?: string[];
}

export interface UpdateDueDatesPayload {
	taskIds: string[];
	dueDate: Date | null;
}

export interface BulkUpdateCategoriesPayload {
	taskIds: string[];
	categoriesToAdd: string[];
	categoriesToRemove: string[];
}

export enum RequestListEvent {
	RefreshDocumentRequestsChannel = `${folderTabKey}.${RequestListEntityType.requestlist}.refresh`,
	RefreshDocumentRequestTasksChannel = `${folderTabKey}.${RequestListEntityType.item}.refresh`,
	OnAttachmentCountChanged = 'urn:sfevent:request-list-pilet:attachment-count',
	OnResourceDeleted = 'urn:event:resource:deleted',
	OnResourceUpdated = 'urn:event:resource:updated',
	OnResourceCreated = 'urn:event:resource:created',
	OnDRLValidationInProgress = 'urn:event:drl-validation:in-progress',
}

export enum DownloadStructure {
	ByTask = 'ByTask',
	ByCategory = 'ByCategory',
	ByAssignee = 'ByAssignee',
	SingleFolder = 'SingleFolder',
}

export interface DownloadResponse {
	downloadUrl: string;
	fileName: string;
}
